import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";

import AssessmentIcon from "@material-ui/icons/Assessment";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import PeopleOutlineIcon from "@material-ui/icons/PeopleOutline";
import ReplayIcon from "@material-ui/icons/Replay";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import TripDetails from "superAdminViews/ride/trips/TripDetails";
import OrderDetails from "shopAdminViews/orders/OrderDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import Panel from "designV2/components/Panel";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { TableSkeleton } from "designV2/components/Skeleton";
import "./superFinance.css";

/* ------------------------------------------------------------------ *
 * GraphQL — unchanged shapes, same two report resolvers the previous
 * screen used.
 * ------------------------------------------------------------------ */

const GET_TRIP_FINANCE = gql`
  query getTripOrdersDetailByAdmin(
    $from: Date!
    $to: Date!
    $driver: ID
    $tripType: TripType
    $justCompletedPayments: Boolean
  ) {
    getTripOrdersDetailByAdmin(
      filters: { from: $from, to: $to, driver: $driver, tripType: $tripType }
      sort: { createdAt: -1 }
      conditions: { justCompletedPayments: $justCompletedPayments }
    ) {
      commissionForSpark
      refundedAmount
      driversIncome
      numberOfTrips
      userTakings
      tripOrders {
        _id
        commission
        HST
        createdAt
        user {
          _id
          fullName
          phoneNumber
        }
        # Exactly what the row and the TripDetails dialog read — the
        # report returns every trip in the range, so anything extra here
        # is paid for on every row.
        trip {
          _id
          ended
          cost
          tripType
          tripDistance
          state
          createdAt
          rate
          staticMapImageUrl
          waitTimePrice
          optionsPrice
          optionsPriceDetails {
            option
            price
          }
          tipValue
          driver {
            _id
            fullName
            phoneNumber
          }
          passengerDetail {
            _id
            fullName
            phoneNumber
          }
        }
      }
    }
  }
`;

const GET_SHOP_FINANCE = gql`
  query getOrdersDetailByAdmin($from: Date!, $to: Date!, $shop: ID, $justCompletedPayments: Boolean) {
    getOrdersDetailByAdmin(
      filters: { from: $from, to: $to, shop: $shop }
      sort: { createdAt: -1 }
      conditions: { justCompletedPayments: $justCompletedPayments }
    ) {
      benefitForSpark
      refundedAmount
      deliveryFees
      shopsIncome
      numberOfOrders
      userTakings
      orders {
        _id
        commission
        HST
        finalPrice
        createdAt
        finished
        status
        rejectedFor
        shipmentModel
        user {
          _id
          fullName
          phoneNumber
        }
        shop {
          _id
          name
        }
        payment {
          _id
        }
        trip {
          _id
          trackId
        }
        tracking {
          trackId
        }
        cart {
          products {
            quantity
            product {
              _id
              title
            }
          }
        }
      }
    }
  }
`;

const GET_DRIVERS = gql`
  query getDriversForReport($fullName: String) {
    getDriversByAdmin(pagination: { limit: 20, skip: 0 }, filters: { fullName: $fullName }) {
      _id
      fullName
      phoneNumber
    }
  }
`;

const GET_SHOPS = gql`
  query getShopsForReport($name: String) {
    getShopsByAdmin(pagination: { limit: 20, skip: 0 }, filters: { name: $name }) {
      _id
      name
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const SEGMENTS = [
  { value: "SHOP", label: "All shops" },
  { value: "RIDE", label: "Rides" },
  { value: "DELIVERY", label: "Deliveries" },
  { value: "INDIVIDUAL_SHOP", label: "One shop" },
  { value: "INDIVIDUAL_DRIVER", label: "One driver" },
];
const SEGMENT_LABELS = SEGMENTS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const TRIP_SEGMENTS = ["RIDE", "DELIVERY", "INDIVIDUAL_DRIVER"];
const isTripSegment = (segment) => TRIP_SEGMENTS.indexOf(segment) !== -1;

const PRESETS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
  { value: "month", label: "This month" },
  { value: "lastMonth", label: "Last month" },
  { value: "custom", label: "Custom range" },
];
const PRESET_LABELS = PRESETS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const rangeForPreset = (preset) => {
  const format = (value) => value.format("YYYY-MM-DD");
  if (preset === "month") {
    return { from: format(moment().startOf("month")), to: format(moment()) };
  }
  if (preset === "lastMonth") {
    const previous = moment().subtract(1, "month");
    return {
      from: format(previous.clone().startOf("month")),
      to: format(previous.clone().endOf("month")),
    };
  }
  const days = parseInt(preset, 10);
  if (!Number.isNaN(days)) {
    return { from: format(moment().subtract(days - 1, "days")), to: format(moment()) };
  }
  return null;
};

const FILTER_DEBOUNCE_MS = 400;

const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

const Figure = ({ label, value, hint, emphasis }) => (
  <div className={`superReportFigure ${emphasis ? "isEmphasis" : ""}`}>
    <span className="superReportFigureLabel">{label}</span>
    <span className="superReportFigureValue">{value}</span>
    {hint && <span className="superReportFigureHint">{hint}</span>}
  </div>
);

/**
 * TotalFinance - the platform revenue report.
 *
 * Replaces commonComponents/finance/Finance.js, which was shared in name
 * only: this screen was its single consumer. Beyond the visual pass, the
 * report now runs itself when the inputs are complete rather than
 * waiting on a Submit button, keeps the results below the controls
 * instead of above them, paginates instead of rendering every row in the
 * range at once, and can be exported.
 *
 * One arithmetic bug is fixed: every figure on the shop side was printed
 * with `.toFixed()` and no argument, so shop income, commission,
 * delivery fees and refunds were all silently rounded to whole dollars
 * while the ride side showed cents.
 */
const TotalFinance = () => {
  const [segment, setSegment] = useState("SHOP");
  const [preset, setPreset] = useState("30");
  const [dateFrom, setDateFrom] = useState(rangeForPreset("30").from);
  const [dateTo, setDateTo] = useState(rangeForPreset("30").to);
  const [completedOnly, setCompletedOnly] = useState(false);

  const [entitySearch, setEntitySearch] = useState("");
  const [entityId, setEntityId] = useState("");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(20);

  const [tripDetail, setTripDetail] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [exporting, setExporting] = useState(false);

  const needsEntity = segment === "INDIVIDUAL_SHOP" || segment === "INDIVIDUAL_DRIVER";
  const tripMode = isTripSegment(segment);

  /* ---------------- entity picker ---------------- */

  const [findDrivers, { data: driversData }] = useLazyQuery(GET_DRIVERS, {
    fetchPolicy: "network-only",
  });
  const [findShops, { data: shopsData }] = useLazyQuery(GET_SHOPS, { fetchPolicy: "network-only" });

  useEffect(() => {
    if (!needsEntity) return undefined;
    const handler = setTimeout(() => {
      if (segment === "INDIVIDUAL_DRIVER") findDrivers({ variables: { fullName: entitySearch.trim() } });
      else findShops({ variables: { name: entitySearch.trim() } });
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsEntity, segment, entitySearch]);

  const entityOptions = useMemo(() => {
    if (segment === "INDIVIDUAL_DRIVER") {
      const drivers = (driversData && driversData.getDriversByAdmin) || [];
      return drivers.map((driver) => ({
        value: driver._id,
        label: driver.phoneNumber ? `${driver.fullName} · ${driver.phoneNumber}` : driver.fullName,
      }));
    }
    const shops = (shopsData && shopsData.getShopsByAdmin) || [];
    return shops.map((shop) => ({ value: shop._id, label: shop.name }));
  }, [segment, driversData, shopsData]);

  const selectedEntity = entityOptions.find((option) => option.value === entityId);

  // Switching between "one shop" and "one driver" invalidates the pick.
  useEffect(() => {
    setEntityId("");
    setEntitySearch("");
  }, [segment]);

  /* ---------------- report ---------------- */

  const ready = Boolean(dateFrom && dateTo && (!needsEntity || entityId));

  const [runTripReport, { data: tripData, loading: tripLoading, error: tripError }] = useLazyQuery(
    GET_TRIP_FINANCE,
    { fetchPolicy: "network-only" }
  );
  const [runShopReport, { data: shopData, loading: shopLoading, error: shopError }] = useLazyQuery(
    GET_SHOP_FINANCE,
    { fetchPolicy: "network-only" }
  );

  useEffect(() => {
    if (!ready) return undefined;
    const handler = setTimeout(() => {
      setPageCount(0);
      if (tripMode) {
        runTripReport({
          variables: {
            from: dateFrom,
            to: dateTo,
            tripType: segment === "INDIVIDUAL_DRIVER" ? undefined : segment,
            driver: segment === "INDIVIDUAL_DRIVER" ? entityId : undefined,
            justCompletedPayments: completedOnly,
          },
        });
      } else {
        runShopReport({
          variables: {
            from: dateFrom,
            to: dateTo,
            shop: segment === "INDIVIDUAL_SHOP" ? entityId : undefined,
            justCompletedPayments: completedOnly,
          },
        });
      }
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, segment, dateFrom, dateTo, entityId, completedOnly]);

  const loading = tripLoading || shopLoading;
  const error = tripError || shopError;

  const report = tripMode
    ? tripData && tripData.getTripOrdersDetailByAdmin
    : shopData && shopData.getOrdersDetailByAdmin;

  const rows = report ? (tripMode ? report.tripOrders : report.orders) || [] : [];

  // HST is not part of the report resolver's totals, so it is summed
  // from the rows it returns — the same thing the previous screen did.
  const hstTotal = rows.reduce((sum, row) => sum + (parseFloat(row.HST) || 0), 0);

  const pageRows = rows.slice(pageCount * perPage, pageCount * perPage + perPage);

  /* ---------------- export ---------------- */

  const handleExport = () => {
    if (!rows.length || exporting) return;
    setExporting(true);
    try {
      const header = tripMode
        ? ["Passenger", "Driver", "Trip type", "Created", "Commission", "HST", "Status"]
        : ["Track ID", "Shop", "Customer", "Created", "Commission", "HST", "Status"];
      const body = rows.map((row) =>
        tripMode
          ? [
              (row.user && row.user.fullName) || "-",
              (row.trip && row.trip.driver && row.trip.driver.fullName) || "-",
              (row.trip && row.trip.tripType) || "-",
              moment(row.createdAt).format("MM/DD/YYYY HH:mm"),
              Number(row.commission || 0).toFixed(2),
              Number(row.HST || 0).toFixed(2),
              row.trip && row.trip.ended ? "completed" : "in progress",
            ]
          : [
              (row.tracking && row.tracking.trackId) || "-",
              (row.shop && row.shop.name) || "-",
              (row.user && row.user.fullName) || "-",
              moment(row.createdAt).format("MM/DD/YYYY HH:mm"),
              Number(row.commission || 0).toFixed(2),
              Number(row.HST || 0).toFixed(2),
              row.finished ? "completed" : "in progress",
            ]
      );
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet([header, ...body]),
        tripMode ? "Trips" : "Orders"
      );
      XLSX.writeFile(wb, `Finance-${SEGMENT_LABELS[segment].replace(/\s/g, "")}-${dateFrom}.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  /* ---------------- render ---------------- */

  const applyPreset = (value) => {
    setPreset(value);
    const range = rangeForPreset(value);
    if (range) {
      setDateFrom(range.from);
      setDateTo(range.to);
    }
  };

  return (
    <div className="page superFinancePage superReportPage">
      <main className="main">
        <PageHeader
          title="Finance report"
          description="What the platform took in over a period, by line of business or for one shop or driver."
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label="Export"
              info={rows.length ? `${rows.length} row${rows.length === 1 ? "" : "s"}` : "Nothing to export"}
              onClick={handleExport}
              disabled={!rows.length}
              loading={exporting}
            />
          }
        />

        <Panel title="Report" subtitle="Results update as soon as the range is complete.">
          <div className="superReportControls">
            <FilterDropdown
              label={`Segment: ${SEGMENT_LABELS[segment]}`}
              options={SEGMENTS}
              value={segment}
              active={false}
              onChange={(e) => setSegment(e.target.value)}
            />
            <FilterDropdown
              label={`Period: ${PRESET_LABELS[preset]}`}
              options={PRESETS}
              value={preset}
              active={false}
              onChange={(e) => applyPreset(e.target.value)}
            />
            <DateRangeFilter
              label={dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : "Dates"}
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => {
                setPreset("custom");
                setDateFrom(e.target.value);
              }}
              onToChange={(e) => {
                setPreset("custom");
                setDateTo(e.target.value);
              }}
            />
            <label className="superReportToggle">
              <input
                type="checkbox"
                checked={completedOnly}
                onChange={() => setCompletedOnly((current) => !current)}
              />
              <span>Only count entries whose payment completed</span>
            </label>
          </div>

          {needsEntity && (
            <div className="superReportControls" style={{ marginTop: "var(--space-4)" }}>
              <SearchField
                placeholder={
                  segment === "INDIVIDUAL_DRIVER" ? "Find a driver by name..." : "Find a shop by name..."
                }
                value={entitySearch}
                onChange={(e) => setEntitySearch(e.target.value)}
              />
              <FilterDropdown
                label={
                  selectedEntity
                    ? selectedEntity.label
                    : segment === "INDIVIDUAL_DRIVER"
                    ? "Choose a driver"
                    : "Choose a shop"
                }
                options={
                  entityOptions.length
                    ? entityOptions
                    : [{ value: "", label: "No matches — try another name" }]
                }
                value={entityId}
                onChange={(e) => setEntityId(e.target.value)}
              />
            </div>
          )}
        </Panel>

        {!ready && (
          <EmptyState
            icon={<AssessmentIcon />}
            title={needsEntity && !entityId ? "Choose who to report on" : "Pick a date range"}
            message={
              needsEntity && !entityId
                ? "Search above and pick a shop or driver, and the report runs on its own."
                : "Set a start and end date, and the report runs on its own."
            }
          />
        )}

        {ready && loading && !report && <TableSkeleton rows={6} columns={7} />}

        {ready && error && (
          <div className="dsFormMessage error" role="alert">
            The report could not be loaded. Try a shorter date range and refresh.
          </div>
        )}

        {ready && report && (
          <React.Fragment>
            <section className="stats">
              <MetricCard
                icon={<AttachMoneyIcon />}
                title="Spark commission"
                value={money(tripMode ? report.commissionForSpark : report.benefitForSpark)}
                tone="success"
                hint={`${dateFrom} to ${dateTo}`}
              />
              <MetricCard
                icon={<PeopleOutlineIcon />}
                title="Customer spend"
                value={money(report.userTakings)}
              />
              <MetricCard
                icon={<AssessmentIcon />}
                title={tripMode ? "Trips" : "Orders"}
                value={tripMode ? report.numberOfTrips || 0 : report.numberOfOrders || 0}
              />
              <MetricCard
                icon={<ReplayIcon />}
                title="Refunded"
                value={money(report.refundedAmount)}
                tone={report.refundedAmount > 0 ? "warning" : "default"}
              />
            </section>

            <Panel title="Breakdown" subtitle={SEGMENT_LABELS[segment]}>
              <div className="superReportBreakdown">
                <Figure label="Customer spend" value={money(report.userTakings)} />
                {tripMode ? (
                  <Figure label="Driver income" value={money(report.driversIncome)} />
                ) : (
                  <React.Fragment>
                    <Figure label="Shop income" value={money(report.shopsIncome)} />
                    <Figure label="Delivery fees" value={money(report.deliveryFees)} />
                  </React.Fragment>
                )}
                <Figure
                  label="Spark commission"
                  value={money(tripMode ? report.commissionForSpark : report.benefitForSpark)}
                  emphasis
                />
                <Figure label="HST collected" value={money(hstTotal)} hint="Summed from the rows below" />
                <Figure label="Refunded" value={money(report.refundedAmount)} />
                <Figure
                  label={tripMode ? "Trips" : "Orders"}
                  value={tripMode ? report.numberOfTrips || 0 : report.numberOfOrders || 0}
                />
              </div>
            </Panel>

            <div className={`tableResponsive ${loading ? "isRefetching" : ""}`}>
              {loading && <span className="superFinanceRefetchSpinner" aria-hidden="true" />}
              <section className="table">
                <div className="tableHeader">
                  <div className="tableHeaderCell">{tripMode ? "Passenger" : "Track ID"}</div>
                  <div className="tableHeaderCell">{tripMode ? "Driver" : "Shop"}</div>
                  <div className="tableHeaderCell">Created</div>
                  <div className="tableHeaderCell">Commission</div>
                  <div className="tableHeaderCell">HST</div>
                  <div className="tableHeaderCell">Status</div>
                  <div className="tableHeaderCell tableHeaderAction">Details</div>
                </div>

                {pageRows.length === 0 ? (
                  <EmptyState
                    icon={<AssessmentIcon />}
                    title={tripMode ? "No trips in this period" : "No orders in this period"}
                    message="Try a wider date range, or turn off the completed-payments filter."
                  />
                ) : (
                  pageRows.map((row) =>
                    tripMode ? (
                      <div className="row" key={row._id}>
                        <div className="superFinanceParty" data-label="Passenger">
                          <span className="superFinancePartyName">
                            {(row.user && row.user.fullName) || "Unnamed"}
                          </span>
                          {row.user && row.user.phoneNumber && (
                            <span className="superFinanceSubtext">{row.user.phoneNumber}</span>
                          )}
                        </div>
                        <div className="superFinanceParty" data-label="Driver">
                          <span className="superFinancePartyName">
                            {(row.trip && row.trip.driver && row.trip.driver.fullName) || "Unassigned"}
                          </span>
                          {row.trip && row.trip.tripType && (
                            <span className="superFinancePartyRole">{row.trip.tripType}</span>
                          )}
                        </div>
                        <span className="updated" data-label="Created">
                          <strong>{formatDate(row.createdAt)}</strong>
                          <span>{formatTime(row.createdAt)}</span>
                        </span>
                        <span className="superFinanceAmount" data-label="Commission">
                          {money(row.commission)}
                        </span>
                        <span className="superFinanceAmount" data-label="HST">
                          {money(row.HST)}
                        </span>
                        <span data-label="Status">
                          <StatusBadge
                            status={row.trip && row.trip.ended ? "success" : "warning"}
                            label={row.trip && row.trip.ended ? "Completed" : "In progress"}
                          />
                        </span>
                        <div className="actions">
                          <IconButton
                            size="small"
                            className="rowActionButton"
                            onClick={() => setTripDetail(row.trip)}
                            title="Trip details"
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : (
                      <div className="row" key={row._id}>
                        <div className="superFinanceRefCell" data-label="Track ID">
                          <span className="superFinanceRef">
                            {(row.tracking && row.tracking.trackId) ||
                              (row.trip && row.trip.trackId) ||
                              "-"}
                          </span>
                        </div>
                        <div className="superFinanceParty" data-label="Shop">
                          <span className="superFinancePartyName">
                            {(row.shop && row.shop.name) || "Unnamed"}
                          </span>
                          {row.user && row.user.fullName && (
                            <span className="superFinanceSubtext">{row.user.fullName}</span>
                          )}
                        </div>
                        <span className="updated" data-label="Created">
                          <strong>{formatDate(row.createdAt)}</strong>
                          <span>{formatTime(row.createdAt)}</span>
                        </span>
                        <span className="superFinanceAmount" data-label="Commission">
                          {money(row.commission)}
                        </span>
                        <span className="superFinanceAmount" data-label="HST">
                          {money(row.HST)}
                        </span>
                        <span data-label="Status">
                          <StatusBadge
                            status={row.finished ? "success" : "warning"}
                            label={row.finished ? "Completed" : "In progress"}
                          />
                        </span>
                        <div className="actions">
                          <IconButton
                            size="small"
                            className="rowActionButton"
                            onClick={() => setOrderDetail(row)}
                            title="Order details"
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    )
                  )
                )}
              </section>
            </div>

            {rows.length > 0 && (
              <PaginationComponent
                currentPage={pageCount}
                pageSize={perPage}
                totalCount={rows.length}
                pageSizeOptions={[20, 50, 100]}
                onPageChange={(newPage) => setPageCount(newPage)}
                onPageSizeChange={(size) => {
                  setPerPage(parseInt(size, 10));
                  setPageCount(0);
                }}
              />
            )}
          </React.Fragment>
        )}
      </main>

      <DialogShell open={Boolean(orderDetail)} onClose={() => setOrderDetail(null)} title="Order details">
        {orderDetail && <OrderDetails order={orderDetail} />}
      </DialogShell>

      <DialogShell
        open={Boolean(tripDetail)}
        onClose={() => setTripDetail(null)}
        title="Trip details"
        subtitle={tripDetail ? `#${tripDetail._id.slice(-8).toUpperCase()}` : ""}
        size="lg"
      >
        {tripDetail && <TripDetails trip={tripDetail} />}
      </DialogShell>
    </div>
  );
};

export default TotalFinance;
